import { useEffect, useMemo, useState } from 'react';
import { approveRefund, listRefunds, markRefunded, rejectRefund } from '../../api/adminRefundsApi';
import { useCurrency } from '../../components/CurrencyContext';

const statusOptions = [
  { value: '', label: 'All statuses' },
  { value: 'REQUESTED', label: 'Requested' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'PROCESSING', label: 'Processing' },
  { value: 'REFUNDED', label: 'Refunded' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'REJECTED', label: 'Rejected' },
];
const queueStatuses = 'REQUESTED,APPROVED,PROCESSING';
const historyStatuses = 'REFUNDED,FAILED,REJECTED';

const badgeStyles = {
  REQUESTED: 'bg-amber-50 text-amber-700 ring-amber-100',
  APPROVED: 'bg-sky-50 text-sky-700 ring-sky-100',
  PROCESSING: 'bg-indigo-50 text-indigo-700 ring-indigo-100',
  REFUNDED: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  FAILED: 'bg-rose-50 text-rose-700 ring-rose-100',
  REJECTED: 'bg-slate-100 text-slate-700 ring-slate-200',
};

const statusLabel = (value) => String(value || 'unknown').replace(/_/g, ' ').toLowerCase();

const RefundBadge = ({ status }) => {
  const key = String(status || '').toUpperCase();
  const cls = badgeStyles[key] || 'bg-slate-100 text-slate-700 ring-slate-200';
  return <span className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset ${cls}`}>{statusLabel(key)}</span>;
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const AdminRefunds = ({ token }) => {
  const { formatCurrency } = useCurrency();
  const [filters, setFilters] = useState({ status: '', search: '', from: '', to: '' });
  const [viewMode, setViewMode] = useState('queue');
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, limit: 20 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const formatMoney = (currency, amountPaise, amountRupees) => {
    if (Number.isFinite(Number(amountPaise))) {
      const value = Number(amountPaise) / 100;
      return formatCurrency(value, currency || 'INR') || `${currency || 'INR'} ${value}`;
    }
    if (Number.isFinite(Number(amountRupees))) {
      return formatCurrency(Number(amountRupees), currency || 'INR') || `${currency || 'INR'} ${amountRupees}`;
    }
    return 'N/A';
  };
  const resolveAmountPaise = (refund) =>
    refund?.amountPaise ?? refund?.amount_paise ?? null;
  const effectiveStatus = filters.status || (viewMode === 'queue' ? queueStatuses : historyStatuses);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [drawerError, setDrawerError] = useState('');
  const [actionLoading, setActionLoading] = useState('');
  const [rejectReason, setRejectReason] = useState('');

  const load = async (page = pagination.page || 1) => {
    setLoading(true);
    setError('');
    try {
      const res = await listRefunds(token, { ...filters, status: effectiveStatus, page, limit: pagination.limit });
      setItems(res.items || []);
      setPagination({ page: res.page || page, limit: res.limit || pagination.limit, total: res.total || 0 });
      return res;
    } catch (err) {
      setError(err.message || 'Failed to load refunds');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const syncSelected = (res) => {
    if (!selectedId || !res?.items) return;
    const updated = res.items.find((item) => item.id === selectedId);
    if (updated) setSelected(updated);
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, viewMode]);

  const applyFilters = () => {
    load(1);
  };

  const openDrawer = (refund) => {
    setDrawerOpen(true);
    setSelectedId(refund.id);
    setSelected(refund);
    setRejectReason(refund.rejectionReason || refund.rejection_reason || '');
    setDrawerError('');
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedId(null);
    setSelected(null);
    setRejectReason('');
    setDrawerError('');
    setActionLoading('');
  };

  const pageCount = useMemo(() => {
    return Math.max(1, Math.ceil((pagination.total || 0) / (pagination.limit || 20)));
  }, [pagination]);

  const statusValue = String(selected?.status || '').toUpperCase();
  const canApprove = statusValue === 'REQUESTED';
  const canReject = ['REQUESTED', 'APPROVED', 'PROCESSING'].includes(statusValue);
  const canMarkRefunded = ['APPROVED', 'PROCESSING'].includes(statusValue);
  const selectedGatewayRefundId = selected?.gatewayRefundId || selected?.gateway_refund_id;

  const handleApprove = async () => {
    if (!selectedId) return;
    setActionLoading('approve');
    setDrawerError('');
    try {
      await approveRefund(token, selectedId);
      const res = await load(pagination.page || 1);
      syncSelected(res);
    } catch (err) {
      setDrawerError(err.message || 'Failed to approve refund');
    } finally {
      setActionLoading('');
    }
  };

  const handleReject = async () => {
    if (!selectedId) return;
    if (!rejectReason.trim()) {
      setDrawerError('Provide a rejection reason.');
      return;
    }
    setActionLoading('reject');
    setDrawerError('');
    try {
      await rejectRefund(token, selectedId, { rejectionReason: rejectReason });
      const res = await load(pagination.page || 1);
      syncSelected(res);
    } catch (err) {
      setDrawerError(err.message || 'Failed to reject refund');
    } finally {
      setActionLoading('');
    }
  };

  const handleMarkRefunded = async () => {
    if (!selectedId) return;
    setActionLoading('refund');
    setDrawerError('');
    try {
      await markRefunded(token, selectedId);
      const res = await load(pagination.page || 1);
      syncSelected(res);
    } catch (err) {
      setDrawerError(err.message || 'Failed to mark refunded');
    } finally {
      setActionLoading('');
    }
  };

  return (
    <div className="admin-card rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <div className="flex items-center justify-between border-b border-slate-200 pb-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-emerald-700">Finance</p>
          <h1 className="admin-page-title text-xl font-bold text-slate-900">Refunds</h1>
          <p className="text-sm text-slate-600">Manage guest refund requests and approvals.</p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => {
            setViewMode('queue');
            setFilters((f) => ({ ...f, status: '' }));
          }}
          className={`rounded-full px-4 py-2 text-xs font-semibold ${
            viewMode === 'queue'
              ? 'bg-emerald-600 text-white'
              : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
          }`}
        >
          Approval queue
        </button>
        <button
          type="button"
          onClick={() => {
            setViewMode('history');
            setFilters((f) => ({ ...f, status: '' }));
          }}
          className={`rounded-full px-4 py-2 text-xs font-semibold ${
            viewMode === 'history'
              ? 'bg-emerald-600 text-white'
              : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
          }`}
        >
          History
        </button>
        {!filters.status && (
          <span className="text-xs text-slate-500">
            Showing {viewMode === 'queue' ? 'requested, approved, processing' : 'refunded, failed, rejected'}
          </span>
        )}
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-5">
        <select
          value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        >
          {statusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <input
          type="search"
          placeholder="Search refund, guest, or payment"
          value={filters.search}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') applyFilters();
          }}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        />
        <input
          type="date"
          value={filters.from}
          onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        />
        <input
          type="date"
          value={filters.to}
          onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        />
        <button
          type="button"
          onClick={applyFilters}
          className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
        >
          Apply filters
        </button>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Refund</th>
              <th className="px-4 py-3">Guest</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td colSpan={6} className="px-4 py-3 text-slate-500">
                  Loading...
                </td>
              </tr>
            )}
            {error && !loading && (
              <tr>
                <td colSpan={6} className="px-4 py-3 text-rose-600">
                  {error}
                </td>
              </tr>
            )}
            {!loading && !items.length && !error && (
              <tr>
                <td colSpan={6} className="px-4 py-3 text-slate-500">
                  No refunds found.
                </td>
              </tr>
            )}
            {items.map((refund) => {
              const refundRef = refund.refundRef || refund.refund_ref || `#${refund.id}`;
              const guestName = refund.guest?.name || refund.guest_name || 'Guest';
              const guestEmail = refund.guest?.email || refund.guest_email || '';
              const createdAt = refund.createdAt || refund.created_at;
              return (
                <tr key={refund.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="text-sm font-semibold text-slate-900">{refundRef}</div>
                    <div className="text-xs text-slate-500">Payment {refund.payment?.paymentRef || refund.paymentId}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    <div className="font-semibold text-slate-900">{guestName}</div>
                    <div className="text-xs text-slate-500">{guestEmail}</div>
                  </td>
                  <td className="px-4 py-3 font-semibold text-slate-900">
                    {formatMoney(refund.currency, resolveAmountPaise(refund), refund.amount)}
                  </td>
                  <td className="px-4 py-3">
                    <RefundBadge status={refund.status} />
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{formatDateTime(createdAt)}</td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => openDrawer(refund)}
                      className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
        <div>
          Page {pagination.page || 1} of {pageCount}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => load(Math.max(1, (pagination.page || 1) - 1))}
            className="rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-50"
            disabled={(pagination.page || 1) <= 1}
          >
            Prev
          </button>
          <button
            type="button"
            onClick={() => load((pagination.page || 1) + 1)}
            className="rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-50"
            disabled={(pagination.page || 1) >= pageCount}
          >
            Next
          </button>
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-slate-900/40"
            onClick={closeDrawer}
            role="button"
            tabIndex={-1}
            aria-label="Close refund drawer"
          />
          <div className="relative ml-auto flex h-full w-full max-w-xl flex-col bg-white shadow-xl">
            <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-emerald-700">Refund</p>
                <h2 className="text-lg font-semibold text-slate-900">
                  {selected?.refundRef || selected?.refund_ref || (selected?.id ? `#${selected.id}` : 'Refund detail')}
                </h2>
                <p className="text-sm text-slate-600">
                  {selected?.guest?.name || selected?.guest_name || 'Guest'}{' '}
                  {selected?.guest?.email ? `(${selected.guest.email})` : ''}
                </p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <RefundBadge status={selected?.status} />
                <button
                  type="button"
                  onClick={closeDrawer}
                  className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {drawerError && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {drawerError}
                </div>
              )}

              {selected && (
                <div className="space-y-5">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-lg border border-slate-200 p-3">
                      <p className="text-xs uppercase tracking-wide text-slate-500">Amount</p>
                      <p className="text-lg font-semibold text-slate-900">
                        {formatMoney(selected.currency, resolveAmountPaise(selected), selected.amount)}
                      </p>
                    </div>
                    <div className="rounded-lg border border-slate-200 p-3">
                      <p className="text-xs uppercase tracking-wide text-slate-500">Created</p>
                      <p className="text-sm font-semibold text-slate-900">
                        {formatDateTime(selected.createdAt || selected.created_at)}
                      </p>
                    </div>
                    <div className="rounded-lg border border-slate-200 p-3">
                      <p className="text-xs uppercase tracking-wide text-slate-500">Type</p>
                      <p className="text-sm font-semibold text-slate-900">{selected.type || 'GUEST_REFUND'}</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 p-3">
                      <p className="text-xs uppercase tracking-wide text-slate-500">Payment</p>
                      <p className="text-sm font-semibold text-slate-900">
                        {selected.payment?.paymentRef || selected.paymentId || 'N/A'}
                      </p>
                    </div>
                    <div className="rounded-lg border border-slate-200 p-3">
                      <p className="text-xs uppercase tracking-wide text-slate-500">Gateway refund</p>
                      <p className="text-sm font-semibold text-slate-900">{selectedGatewayRefundId || 'N/A'}</p>
                    </div>
                  </div>

                  {selected.reason && (
                    <div className="rounded-lg border border-slate-200 p-3 text-sm text-slate-700">
                      <p className="text-xs uppercase tracking-wide text-slate-500">Reason</p>
                      <p className="mt-1">{selected.reason}</p>
                    </div>
                  )}
                  {(selected.rejectionReason || selected.rejection_reason) && (
                    <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                      <p className="text-xs uppercase tracking-wide text-rose-500">Rejection</p>
                      <p className="mt-1">{selected.rejectionReason || selected.rejection_reason}</p>
                    </div>
                  )}

                  <div className="rounded-lg border border-slate-200 p-3">
                    <p className="text-sm font-semibold text-slate-900">Host details</p>
                    <div className="mt-2 text-sm text-slate-700">
                      <p>{selected.host?.name || selected.host_name || 'Host'}</p>
                      <p>{selected.host?.email || selected.host_email || 'N/A'}</p>
                      <p className="text-xs text-slate-500">Host ID: {selected.hostId}</p>
                    </div>
                  </div>

                  <div className="rounded-lg border border-slate-200 p-3">
                    <p className="text-sm font-semibold text-slate-900">Actions</p>
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      <button
                        type="button"
                        onClick={handleApprove}
                        disabled={!canApprove || actionLoading}
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                      >
                        {actionLoading === 'approve' ? 'Approving...' : 'Approve'}
                      </button>
                      <button
                        type="button"
                        onClick={handleReject}
                        disabled={!canReject || actionLoading}
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                      >
                        {actionLoading === 'reject' ? 'Rejecting...' : 'Reject'}
                      </button>
                      <div className="sm:col-span-2">
                        <textarea
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          placeholder="Rejection reason"
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                          rows={3}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleMarkRefunded}
                        disabled={!canMarkRefunded || actionLoading}
                        className="sm:col-span-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                      >
                        {actionLoading === 'refund' ? 'Marking refunded...' : 'Mark refunded (testing)'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {!selected && <p className="text-sm text-slate-500">No refund selected.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRefunds;
